import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import StorefrontLayout from "../components/storefront/Layout";
import Breadcrumbs, { breadcrumbJsonLd } from "../components/storefront/Breadcrumbs";
import ProductCard from "../components/storefront/ProductCard";
import { Pagination } from "./Shop";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { Zap } from "lucide-react";

const CONTROL_SEL = "h-9 px-2 text-xs bg-transparent border border-white/15 rounded-sm text-zinc-300 focus:outline-none focus:border-red-500 cursor-pointer";

export default function EVGlass() {
  const [evBrands, setEvBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(24);
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/vehicles/makes").then(({ data }) => {
      const groupOf = (b) => b.brand_group || (b.is_exotic ? "exotic_luxury" : "other");
      setEvBrands((data.makes || []).filter((b) => groupOf(b) === "ev_specialty"));
    });
  }, []);

  useEffect(() => { setPage(1); }, [sort, perPage]);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams({ brand_group: "ev_specialty", sort, limit: String(perPage), page: String(page) });
    api.get(`/products?${p.toString()}`).then(({ data }) => {
      setProducts(data.products || []);
      setTotal(data.total ?? (data.products || []).length);
      setTotalPages(data.total_pages ?? 1);
      setLoading(false);
    });
  }, [sort, perPage, page]);

  const crumbs = [{ label: "Home", to: "/" }, { label: "EV Specialty Glass" }];
  useSeo({
    title: "EV Specialty Glass — Tesla, Lucid, VinFast & More | Lecis Auto Glass",
    description: "Specialty auto glass for electric vehicles — side mirrors, windshields, backglass and sunroof glass for Tesla, Lucid, VinFast and other EV brands. Shipped nationwide.",
    canonicalPath: "/ev-glass",
    jsonLd: { breadcrumb: breadcrumbJsonLd(crumbs) },
  });

  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);

  return (
    <StorefrontLayout>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 md:py-24" data-testid="ev-glass-page">
        <Breadcrumbs items={crumbs} />
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold mb-3">
          <Zap size={12} /> EV Specialty
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium mb-4">
          EV Specialty Glass
        </h1>
        <p className="text-base text-zinc-400 max-w-2xl leading-relaxed mb-10">
          Precision replacement glass engineered for electric vehicles — side mirrors, windshields,
          backglass and panoramic sunroof glass for the latest EV brands, shipped nationwide.
        </p>

        {/* EV brand cards */}
        {evBrands.length > 0 && (
          <div className="mb-14" data-testid="ev-brands">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">EV brands</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {evBrands.map((b) => (
                <Link
                  key={b.id}
                  to={`/${b.slug}`}
                  data-testid={`ev-brand-${b.slug}`}
                  className="group bg-[#0d0d0d] border border-white/5 hover:border-red-500/50 rounded-md p-8 transition"
                >
                  <div className="font-display text-xl tracking-tight group-hover:text-red-500 transition">{b.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-2">View Parts →</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* EV product grid */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap border-t border-white/5 pt-10">
          <div className="text-xs text-zinc-500" data-testid="ev-count">
            {loading ? "Loading…" : total === 0 ? "0 products" : <span data-testid="ev-showing-range">Showing {rangeStart}–{rangeEnd} of {total} products</span>}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-zinc-500">
              <span className="hidden sm:inline">Sort</span>
              <select data-testid="ev-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className={CONTROL_SEL}>
                <option value="newest">Newest</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="name">Product Name</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-zinc-500">
              <span className="hidden sm:inline">Products per page</span>
              <select data-testid="ev-perpage-select" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} className={CONTROL_SEL}>
                {[12, 24, 48, 96].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
        </div>

        {loading ? (
          <div className="text-zinc-500 py-20 text-center">Loading…</div>
        ) : products.length === 0 ? (
          <div className="text-center text-zinc-500 py-20 border border-dashed border-white/10 rounded-md" data-testid="ev-empty">
            No EV products are available yet. <Link to="/shop" className="text-red-500 hover:text-red-400">Browse all glass →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="ev-product-grid">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}

        {!loading && totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages} onChange={(n) => { setPage(n); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
        )}
      </div>
    </StorefrontLayout>
  );
}
